import type { CrremDemandPoint, CrremSettings, CrremYearlyPoint, EnergyAnalysisRow } from "../model/types";

import {
  DEFAULT_CRREM_SETTINGS,
  isCrremDisplayMetric,
  isCrremTrendMethod,
} from "../model/constants";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const readBoolean = (value: unknown, fallback: boolean) => (typeof value === "boolean" ? value : fallback);

const readFiniteNumber = (value: unknown) => (typeof value === "number" && Number.isFinite(value) ? value : undefined);

const readString = (value: unknown) => (typeof value === "string" && value.length > 0 ? value : undefined);

export const parseCrremSettings = (payload: unknown): CrremSettings => {
  const source = isRecord(payload) && isRecord(payload.settings) ? payload.settings : payload;
  if (!isRecord(source)) return DEFAULT_CRREM_SETTINGS;
  return {
    climateCorrectionEnabled: readBoolean(source.climateCorrectionEnabled, DEFAULT_CRREM_SETTINGS.climateCorrectionEnabled),
    coverageCorrectionEnabled: readBoolean(
      source.coverageCorrectionEnabled,
      DEFAULT_CRREM_SETTINGS.coverageCorrectionEnabled
    ),
    displayMetric: isCrremDisplayMetric(source.displayMetric)
      ? source.displayMetric
      : DEFAULT_CRREM_SETTINGS.displayMetric,
    trendMethod: isCrremTrendMethod(source.trendMethod) ? source.trendMethod : DEFAULT_CRREM_SETTINGS.trendMethod,
    vacancyCorrectionEnabled: readBoolean(source.vacancyCorrectionEnabled, DEFAULT_CRREM_SETTINGS.vacancyCorrectionEnabled),
  };
};

const parseDemandPoint = (value: unknown): CrremDemandPoint | undefined => {
  if (!isRecord(value)) return undefined;
  const year = readFiniteNumber(value.year);
  const pointValue = readFiniteNumber(value.value);
  const utility = readString(value.utility);
  const useType = readString(value.useType);
  if (year === undefined || pointValue === undefined || !utility || !useType) return undefined;
  return { useType, utility, value: pointValue, year };
};

export const parseDemandPointRows = (source: unknown): EnergyAnalysisRow[] => {
  if (!Array.isArray(source)) return [];
  const rows: EnergyAnalysisRow[] = [];
  source.forEach((item) => {
    const point = parseDemandPoint(item);
    if (!point) return;
    rows.push({
      id: `${point.year}-${point.utility}-${point.useType}`,
      useType: point.useType,
      utility: point.utility,
      value: point.value,
      year: point.year,
    });
  });
  return rows;
};

export const parseDemandRows = (payload: unknown): EnergyAnalysisRow[] => {
  if (!isRecord(payload)) return [];
  const corrected = parseDemandPointRows(payload.corrected);
  return corrected.length > 0 ? corrected : parseDemandPointRows(payload.baseline);
};

export const parseDemandUnit = (payload: unknown, fallback: string) => {
  if (!isRecord(payload)) return fallback;
  return readString(payload.unit) ?? fallback;
};

export const parseLastReportedYear = (payload: unknown) => {
  if (!isRecord(payload)) return undefined;
  return readFiniteNumber(payload.lastReportedYear);
};

export const sumDemandByYear = (source: unknown): CrremYearlyPoint[] => {
  const totals = new Map<number, number>();
  if (!Array.isArray(source)) return [];
  source.forEach((item) => {
    const point = parseDemandPoint(item);
    if (point) {
      totals.set(point.year, (totals.get(point.year) ?? 0) + point.value);
      return;
    }
    if (!isRecord(item)) return;
    const year = readFiniteNumber(item.year);
    const value = readFiniteNumber(item.value);
    if (year === undefined || value === undefined) return;
    totals.set(year, (totals.get(year) ?? 0) + value);
  });
  return [...totals.entries()]
    .sort((left, right) => left[0] - right[0])
    .map(([year, value]) => ({ value, year }));
};

export const parseDemandYearlySeries = (payload: unknown) => {
  if (!isRecord(payload)) return { baseline: [], corrected: [] };
  return {
    baseline: sumDemandByYear(payload.baseline),
    corrected: sumDemandByYear(payload.corrected),
  };
};
